import { type MondayItemColumn } from "./memoization-cache";

/**
 * Calculate which columns should be pinned based on element state and available columns.
 *
 * If pinnedColumnIds is provided (user has explicitly set pinned columns), returns those filtered columns.
 * Otherwise, returns default pinned columns: first 2 status/date columns + first people column (if available).
 *
 * The people column is always placed last if present.
 * Maximum of 3 pinned columns total (2 other + 1 people).
 */
export function calculatePinnedColumns(
  columnValues: MondayItemColumn[],
  pinnedColumnIds: string[] | undefined
): MondayItemColumn[] {
  if (!columnValues) return [];

  // If user has explicitly set pinned columns, use those
  if (pinnedColumnIds) {
    const pinnedIdSet = new Set(pinnedColumnIds);
    const pinnedColumns = columnValues.filter((col) => pinnedIdSet.has(col.id));
    const pinnedPeopleColumn = pinnedColumns.find((col) => col.type === "people");
    const allOtherColumns = pinnedColumns.filter((col) => col.id !== pinnedPeopleColumn?.id);

    if (pinnedPeopleColumn) {
      return [...allOtherColumns.slice(0, 2), pinnedPeopleColumn];
    }

    return allOtherColumns.slice(0, 2);
  }

  // Fallback to default pinned columns: first 2 status/date columns + people column
  const statusOrDate = columnValues
    .filter((columnValue) => columnValue.type === "status" || columnValue.type === "date")
    .slice(0, 2);
  const person = columnValues.find((columnValue) => columnValue.type === "people");

  return [...statusOrDate, person].filter((col): col is MondayItemColumn => col !== undefined);
}

export function getColumnDisplayValue(column: MondayItemColumn): string {
  // We cast the value to any aince it literally can be anything
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const value = column.value as any;

  switch (column.type) {
    case "status": {
      return value?.text ?? value?.label ?? "Unset";
    }

    case "date": {
      return value?.date ?? value ?? "-";
    }

    case "file": {
      return value?.map((f: { name: string }) => f.name).join(", ") ?? "-";
    }

    case "dropdown": {
      // For some reason the root mapping functions do not handle empty
      // dropdown values very well. So we need to handle it here.

      if (value?.startsWith('{"ids":[')) {
        return "-"; // Empty value instead of metadata
      }

      // If the mapping had values, the root mapping function will
      // parse their labels separated by commas. We just return that.
      return value ?? "-";
    }

    default: {
      if (typeof column.value === "string") {
        return column.value;
      }

      if (column.value && typeof column.value === "object") {
        // If it's an object with a text or label property
        const valueObject = column.value as Record<string, unknown>;
        if ("text" in valueObject) return String(valueObject.text ?? "");
        if ("label" in valueObject) return String(valueObject.label ?? "");
      }

      return String(column.value ?? "-");
    }
  }
}
